import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useCallback } from 'react'
import { ChatLayout } from '../components/layout/ChatLayout'
import { ConversationContent } from '../features/chat/components/ConversationContent'
import { ChatInputSimple } from '../features/chat/components/ChatInputSimple'
import { useAutoFocus } from '../hooks'
import { ThoughtProcessPanel } from '../features/chat/components/ThoughtProcessPanel'
import { ConversationErrorBoundary } from '../components/common/ConversationErrorBoundary'
import { useConversationDetailWithStore } from '../hooks/useConversationDetail'
import { useMessagesWithStore } from '../hooks/useMessages'
import { useChatStore } from '../stores/chatStore'

export const Route = createFileRoute('/chat/$conversationId')({
  component: ConversationRoute,
  beforeLoad: ({ params }) => {
    // Validate conversation ID format - should be a UUID
    if (!params.conversationId || params.conversationId.length < 36) {
      throw new Error('Invalid conversation ID format')
    }
  },
  // Remove blocking loader to allow component-level loading states
  // The component's useQuery will handle loading, allowing us to see loading skeletons
  loader: async ({ params: { conversationId } }) => {
    // Don't block - let the component handle loading
    return null;
  },
  errorComponent: ({ error }: { error: Error }) => (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>Failed to load conversation</h2>
      <p>{error.message}</p>
    </div>
  ),
})

function ConversationRoute() {
  const { conversationId } = Route.useParams()

  // Use the enhanced hook that integrates with store
  const conversationQuery = useConversationDetailWithStore(conversationId);

  // Thought process store state
  const { isOpen, currentThoughtProcess, closePanel } = useMessagesWithStore({conversationID: conversationId, messageID: ''});

  const renderThoughtProcess = useCallback(() => {
    return (
      <ThoughtProcessPanel
        thoughtProcess={currentThoughtProcess}
        onClose={closePanel}
      />
    )
  }, [currentThoughtProcess, closePanel]);

  // Clear attachments when switching conversations
  const { clearAttachments } = useChatStore();
  useEffect(() => {
    clearAttachments();
  }, [conversationId, clearAttachments]);

  // Refs for scroll area to enable auto-scroll functionality
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  
  // Auto-focus the chat input when conversation loads successfully
  const chatInputRef = useAutoFocus<HTMLTextAreaElement>({
    condition: conversationQuery.isSuccess && !conversationQuery.isFetching,
    delay: true // Use requestAnimationFrame for smooth UX
  });


  // Note: ChatInputSimple now handles message sending internally via useChatActions

  return (
    <ConversationErrorBoundary conversationId={conversationId}>
      <ChatLayout
        scrollAreaRef={scrollAreaRef}
        viewportRef={viewportRef}
        showThoughtProcess={isOpen}
        thoughtProcessPanel={
            renderThoughtProcess()
        }
        footerContent={
          <ChatInputSimple
            ref={chatInputRef}
            conversationId={conversationId}
            showControls={true}
            query={conversationQuery}
          />
        }
      >
        <ConversationContent
          conversationId={conversationId}
          query={conversationQuery}
          viewportRef={viewportRef}
        />
      </ChatLayout>
    </ConversationErrorBoundary>
  );
}
